import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, Edit, Workflow, X, Loader2, GripVertical, Clock, Factory,
  FileText, Blend, Beaker, RefreshCw, Layers, Minimize2, Scissors,
  Sparkles, Package, Circle, Check, AlertCircle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'

interface Etapa {
  id: number
  codigo: string
  nome: string
  descricao: string | null
  ordem: number
  cor: string
  icone: string
  tempo_padrao_minutos: number
  requer_maquina: boolean
  requer_quantidade: boolean
  permite_refugo: boolean
  final: boolean
  ativo: boolean
}

const ICONES: Record<string, any> = {
  FileText, Blend, Beaker, RefreshCw, Layers, Minimize2, Scissors,
  Sparkles, Package, Circle, Factory, Clock, Check,
}

const ICONES_OPCOES = Object.keys(ICONES)
const CORES_PRESET = [
  '#6c757d', '#fd7e14', '#8b5cf6', '#f59e0b', '#eab308',
  '#dc2626', '#16a34a', '#7c3aed', '#0891b2', '#0284c7',
]

export default function IndEtapasPage() {
  const qc = useQueryClient()
  const [modal, setModal] = useState<Etapa | null | 'new'>(null)
  const [draggedId, setDraggedId] = useState<number | null>(null)
  const [dragOverId, setDragOverId] = useState<number | null>(null)
  const [localOrder, setLocalOrder] = useState<Etapa[] | null>(null)

  const { data = [], isLoading } = useQuery<Etapa[]>({
    queryKey: ['ind-etapas'],
    queryFn: async () => (await api.get('/industrial/etapas')).data,
  })

  const seedBorracha = useMutation({
    mutationFn: async () => (await api.post('/industrial/etapas/seed-borracha')).data,
    onSuccess: (r) => {
      toast.success(`${r.total} etapa(s) padrão criadas`)
      qc.invalidateQueries({ queryKey: ['ind-etapas'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro no seed'),
  })

  const reordenar = useMutation({
    mutationFn: async (ids: number[]) =>
      (await api.post('/industrial/etapas/reordenar', ids)).data,
    onSuccess: () => {
      toast.success('Fluxo atualizado')
      qc.invalidateQueries({ queryKey: ['ind-etapas'] })
      setLocalOrder(null)
    },
    onError: () => {
      toast.error('Erro ao reordenar')
      setLocalOrder(null)
    },
  })

  const etapas = localOrder || data

  const onDragStart = (id: number) => setDraggedId(id)
  const onDragOver = (e: React.DragEvent, id: number) => {
    e.preventDefault()
    setDragOverId(id)
  }
  const onDrop = (targetId: number) => {
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null); setDragOverId(null); return
    }
    const arr = [...etapas]
    const fromIdx = arr.findIndex((e) => e.id === draggedId)
    const toIdx = arr.findIndex((e) => e.id === targetId)
    if (fromIdx < 0 || toIdx < 0) return
    const [moved] = arr.splice(fromIdx, 1)
    arr.splice(toIdx, 0, moved)
    setLocalOrder(arr)
    setDraggedId(null); setDragOverId(null)
    // Envia nova ordem pra API
    reordenar.mutate(arr.map((e) => e.id))
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Workflow className="text-brand-600" /> Fluxo de Produção (Etapas)
          </h1>
          <p className="text-sm text-ink-500">Configure o fluxo do chão de fábrica. Arraste para reordenar.</p>
        </div>
        <div className="flex gap-2">
          {data.length === 0 && (
            <button className="btn-ghost" onClick={() => seedBorracha.mutate()} disabled={seedBorracha.isPending}>
              {seedBorracha.isPending ? <Loader2 className="animate-spin" size={14} /> : '🏭'} Usar fluxo padrão borracha
            </button>
          )}
          <button className="btn-primary" onClick={() => setModal('new')}>
            <Plus size={14} /> Nova etapa
          </button>
        </div>
      </div>

      {isLoading && <div className="text-center py-10 text-ink-400">Carregando…</div>}

      {!isLoading && etapas.length === 0 && (
        <div className="bg-white border-2 border-dashed border-ink-200 rounded-lg p-10 text-center">
          <Workflow className="mx-auto mb-3 text-ink-300" size={48} />
          <h3 className="font-semibold text-ink-700 mb-1">Nenhuma etapa configurada</h3>
          <p className="text-sm text-ink-500 mb-4">
            Configure o fluxo do seu chão de fábrica. Pra começar rápido, use o fluxo padrão de borracha (9 etapas).
          </p>
          <button className="btn-primary" onClick={() => seedBorracha.mutate()} disabled={seedBorracha.isPending}>
            🏭 Usar fluxo padrão borracha
          </button>
        </div>
      )}

      {/* Fluxo visual com drag-and-drop */}
      {etapas.length > 0 && (
        <div className="bg-white border border-ink-200 rounded-lg p-4">
          <div className="flex flex-wrap gap-2 items-center">
            {etapas.map((e, idx) => {
              const Icon = ICONES[e.icone] || Circle
              const isDragOver = dragOverId === e.id
              return (
                <div key={e.id} className="flex items-center gap-2">
                  <div
                    draggable
                    onDragStart={() => onDragStart(e.id)}
                    onDragOver={(ev) => onDragOver(ev, e.id)}
                    onDrop={() => onDrop(e.id)}
                    onDragEnd={() => { setDraggedId(null); setDragOverId(null) }}
                    onClick={() => setModal(e)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 cursor-move transition min-w-[160px]
                      ${isDragOver ? 'border-brand-500 bg-brand-50 scale-105' : 'border-transparent hover:border-ink-300'}
                      ${draggedId === e.id ? 'opacity-40' : ''}
                    `}
                    style={{ backgroundColor: isDragOver ? undefined : `${e.cor}15` }}
                  >
                    <GripVertical size={12} className="text-ink-400" />
                    <div
                      className="flex items-center justify-center w-9 h-9 rounded-full text-white flex-shrink-0"
                      style={{ backgroundColor: e.cor }}
                    >
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold text-ink-900 truncate">{e.nome}</div>
                      <div className="text-[10px] text-ink-500 flex items-center gap-1">
                        <Clock size={8} />{e.tempo_padrao_minutos}min
                        {e.requer_maquina && <span title="Requer máquina">· <Factory size={8} className="inline" /></span>}
                        {e.final && <span className="text-emerald-600" title="Gera estoque">· ✓</span>}
                      </div>
                    </div>
                  </div>
                  {idx < etapas.length - 1 && <span className="text-ink-300">→</span>}
                </div>
              )
            })}
          </div>
          <div className="mt-3 pt-3 border-t border-ink-100 text-xs text-ink-500">
            <span className="mr-3">🔨 Arraste um bloco pra reordenar</span>
            <span className="mr-3">👆 Clique num bloco pra editar</span>
            <span>✓ final = etapa que gera produto acabado (dispara estoque)</span>
          </div>
        </div>
      )}

      {/* Lista detalhada (tabela) */}
      {etapas.length > 0 && (
        <div className="bg-white border border-ink-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-ink-50">
              <tr>
                <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2 w-12">#</th>
                <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Código</th>
                <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Nome</th>
                <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Tempo</th>
                <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Requer máquina</th>
                <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Permite refugo</th>
                <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Final</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {etapas.map((e) => {
                const Icon = ICONES[e.icone] || Circle
                return (
                  <tr key={e.id}
                    onClick={() => setModal(e)}
                    className="hover:bg-violet-50 cursor-pointer">
                    <td className="px-3 py-2 text-center text-xs font-mono">{e.ordem}</td>
                    <td className="px-3 py-2 font-mono text-xs">{e.codigo}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div
                          className="flex items-center justify-center w-6 h-6 rounded-full text-white flex-shrink-0"
                          style={{ backgroundColor: e.cor }}
                        >
                          <Icon size={12} />
                        </div>
                        <span className="font-medium">{e.nome}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center text-xs">{e.tempo_padrao_minutos}min</td>
                    <td className="px-3 py-2 text-center">{e.requer_maquina ? '✓' : '—'}</td>
                    <td className="px-3 py-2 text-center">{e.permite_refugo ? '✓' : '—'}</td>
                    <td className="px-3 py-2 text-center">{e.final ? <span className="text-emerald-600 font-bold">✓</span> : '—'}</td>
                    <td className="px-3 py-2" onClick={ev => ev.stopPropagation()}>
                      <button onClick={() => setModal(e)} className="text-ink-500 hover:text-brand-600 p-1">
                        <Edit size={14} />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <EtapaModal
          etapa={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSuccess={() => { qc.invalidateQueries({ queryKey: ['ind-etapas'] }); setModal(null) }}
        />
      )}
    </div>
  )
}


function EtapaModal({ etapa, onClose, onSuccess }: { etapa: Etapa | null; onClose: () => void; onSuccess: () => void }) {
  const [form, setForm] = useState({
    codigo: etapa?.codigo || '',
    nome: etapa?.nome || '',
    descricao: etapa?.descricao || '',
    cor: etapa?.cor || '#6c757d',
    icone: etapa?.icone || 'Circle',
    tempo_padrao_minutos: etapa?.tempo_padrao_minutos || 60,
    requer_maquina: etapa?.requer_maquina || false,
    requer_quantidade: etapa?.requer_quantidade ?? true,
    permite_refugo: etapa?.permite_refugo ?? true,
    final: etapa?.final || false,
  })

  const mut = useMutation({
    mutationFn: async () => {
      if (etapa) return (await api.put(`/industrial/etapas/${etapa.id}`, form)).data
      return (await api.post('/industrial/etapas', form)).data
    },
    onSuccess: () => {
      toast.success(etapa ? 'Etapa atualizada' : 'Etapa cadastrada')
      onSuccess()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const valido = form.codigo.trim() && form.nome.trim()
  const IconPreview = ICONES[form.icone] || Circle

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-ink-200 p-4 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Workflow size={18} /> {etapa ? `Editar ${etapa.nome}` : 'Nova etapa'}
          </h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          {/* Preview do bloco */}
          <div className="flex items-center justify-center gap-2 py-3 bg-ink-50 rounded-lg">
            <div className="flex items-center justify-center w-10 h-10 rounded-full text-white" style={{ backgroundColor: form.cor }}>
              <IconPreview size={18} />
            </div>
            <div className="text-sm font-bold">{form.nome || 'Nome da etapa'}</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Código *</label>
              <input className="input" value={form.codigo}
                onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase().replace(/\s/g, '_') })}
                placeholder="Ex: PRENSA" />
            </div>
            <div>
              <label className="label">Nome *</label>
              <input className="input" value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Ex: Prensa / Vulcanização" />
            </div>
          </div>
          <div>
            <label className="label">Descrição</label>
            <input className="input" value={form.descricao}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Cor</label>
              <div className="flex gap-1 flex-wrap">
                {CORES_PRESET.map((c) => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, cor: c })}
                    className={`w-7 h-7 rounded-full border-2 ${form.cor === c ? 'border-ink-900' : 'border-white'}`}
                    style={{ backgroundColor: c }} />
                ))}
                <input type="color" className="w-7 h-7 rounded cursor-pointer"
                  value={form.cor} onChange={(e) => setForm({ ...form, cor: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="label">Ícone</label>
              <select className="input" value={form.icone}
                onChange={(e) => setForm({ ...form, icone: e.target.value })}>
                {ICONES_OPCOES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label">Tempo padrão (minutos)</label>
            <input type="number" min="1" className="input" value={form.tempo_padrao_minutos}
              onChange={(e) => setForm({ ...form, tempo_padrao_minutos: Number(e.target.value) || 1 })} />
          </div>
          <div className="grid grid-cols-2 gap-2 pt-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.requer_maquina}
                onChange={(e) => setForm({ ...form, requer_maquina: e.target.checked })} />
              Requer máquina
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.requer_quantidade}
                onChange={(e) => setForm({ ...form, requer_quantidade: e.target.checked })} />
              Registra quantidade
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.permite_refugo}
                onChange={(e) => setForm({ ...form, permite_refugo: e.target.checked })} />
              Permite refugo
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.final}
                onChange={(e) => setForm({ ...form, final: e.target.checked })} />
              <span className="font-semibold text-emerald-700">Etapa final (gera estoque)</span>
            </label>
          </div>
          {form.final && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs p-2 rounded flex items-start gap-1">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              <span>Quando esta etapa for apontada, dispara automaticamente a entrada do produto acabado no estoque.</span>
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <button className="btn-ghost flex-1" onClick={onClose}>Cancelar</button>
            <button className="btn-primary flex-1" disabled={!valido || mut.isPending}
              onClick={() => mut.mutate()}>
              {mut.isPending ? <Loader2 className="animate-spin" size={14} /> : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
